import {
  insertPositionToIndex,
  type InsertToPosition,
} from '@blocksuite/affine-shared/utils';
import { computed, type ReadonlySignal } from '@preact/signals-core';

import type { GroupBy, GroupProperty } from '../../core/common/types.js';
import type { DataSourceWindowRequest } from '../../core/data-source/base.js';
import { clampDataSourceWindowOffset } from '../../core/data-source/window.js';
import { generateDefaultValues } from '../../core/filter/generate-default-values.js';
import { FilterTrait, filterTraitKey } from '../../core/filter/trait.js';
import type { FilterGroup } from '../../core/filter/types.js';
import {
  emptyFilterGroup,
  normalizeFilterGroup,
} from '../../core/filter/utils.js';
import { reorderGroupProperties } from '../../core/group-by/property-order.js';
import {
  group2TraitKey,
  GroupTrait,
  groupTraitKey,
  sortByManually,
} from '../../core/group-by/trait.js';
import { fromJson } from '../../core/property/utils';
import { PropertyBase } from '../../core/view-manager/property.js';
import type { Row } from '../../core/view-manager/row.js';
import {
  SingleViewBase,
  type SingleViewPropertiesWindow,
} from '../../core/view-manager/single-view.js';
import type { KanbanViewData } from './define.js';
import {
  createDefaultKanbanL1GroupBy,
  isKanbanL1Groupable,
} from './groupable.js';
import { orderKanbanPropertyIdsForDefault } from './property-defaults.js';

export { group2TraitKey };

export const KANBAN_COLUMN_DEFAULT_WIDTH = 260;
export const KANBAN_COLUMN_MAX_WIDTH = KANBAN_COLUMN_DEFAULT_WIDTH * 2;

export const normalizeKanbanColumnWidth = (width: number | undefined) => {
  const normalizedWidth = Number.isFinite(width)
    ? width
    : KANBAN_COLUMN_DEFAULT_WIDTH;
  return Math.min(
    KANBAN_COLUMN_MAX_WIDTH,
    Math.max(
      KANBAN_COLUMN_DEFAULT_WIDTH,
      Math.round(normalizedWidth ?? KANBAN_COLUMN_DEFAULT_WIDTH)
    )
  );
};

export class KanbanSingleView extends SingleViewBase<KanbanViewData> {
  propertiesRaw$: ReadonlySignal<KanbanColumn[]> = computed(() => {
    const needShow = new Set(this.getBoundedPropertyIdsForView());
    const result: string[] = [];
    this.data$.value?.columns.forEach(v => {
      if (needShow.has(v.id)) {
        result.push(v.id);
        needShow.delete(v.id);
      }
    });
    result.push(
      ...orderKanbanPropertyIdsForDefault(this.manager.dataSource, [
        ...needShow,
      ])
    );
    return result.map(id => this.propertyGetOrCreate(id));
  });

  properties$: ReadonlySignal<KanbanColumn[]> = computed(() => {
    return this.propertiesRaw$.value.filter(property => !property.hide$.value);
  });

  columnWidth$ = computed(() =>
    normalizeKanbanColumnWidth(this.data$.value?.columnWidth)
  );

  detailProperties$: ReadonlySignal<KanbanColumn[]> = computed(() => {
    return this.propertiesRaw$.value.filter(
      property => property.type$.value !== 'title'
    );
  });

  filter$ = computed(() => {
    return normalizeFilterGroup(this.data$.value?.filter);
  });

  filterTrait = this.traitSet(
    filterTraitKey,
    new FilterTrait(this.filter$, this, {
      filterSet: filter => {
        this.dataUpdate(() => {
          return {
            filter,
          };
        });
      },
    })
  );

  groupBy$ = computed(() => {
    const groupBy = this.data$.value?.groupBy;
    if (
      groupBy &&
      isKanbanL1Groupable(this.manager.dataSource, groupBy.columnId)
    ) {
      return groupBy;
    }
    return createDefaultKanbanL1GroupBy(this.manager.dataSource);
  });

  groupBy2$ = computed(() => {
    return this.data$.value?.groupBy2;
  });

  protected override getProjectionFilter(): FilterGroup | undefined {
    return this.filter$.value;
  }

  protected override getProjectionGroupBy(): readonly GroupBy[] {
    return [this.groupBy$.value, this.groupBy2$.value].filter(
      (groupBy): groupBy is GroupBy => !!groupBy
    );
  }

  groupCollapsed2$ = computed(() => {
    return this.data$.value?.groupCollapsed2 ?? [];
  });

  private buildGroupOps(field: 'groupProperties' | 'groupProperties2') {
    const readList = () => (this.view?.[field] ?? []) as GroupProperty[];
    return {
      groupBySet: (groupBy: GroupBy | undefined) => {
        this.dataUpdate(() => {
          if (field === 'groupProperties2') {
            if (!groupBy) {
              // Removing the sub-grouping: drop the groupBy2 key
              // entirely (the board falls back to the flat 1-D
              // layout) and reset the per-group state that only
              // makes sense relative to the removed property.
              return {
                groupBy2: undefined,
                groupProperties2: [],
                groupCollapsed2: [],
              };
            }
            return { groupBy2: groupBy };
          }
          return { groupBy };
        });
      },
      sortGroup: (ids: string[], asc?: boolean) => {
        const sorted = sortByManually(
          ids,
          v => v,
          readList().map(v => v.key)
        );
        return asc === false ? sorted.reverse() : sorted;
      },
      sortRow: (key: string, rows: Row[]) => {
        const property = readList().find(v => v.key === key);
        return sortByManually(
          rows,
          v => v.rowId,
          property?.manuallyCardSort ?? []
        );
      },
      changeGroupSort: (keys: string[]) => {
        this.dataUpdate(() => ({
          [field]: reorderGroupProperties(readList(), keys),
        }));
      },
      changeRowSort: (
        groupKeys: string[],
        groupKey: string,
        keys: string[]
      ) => {
        // Preserve the user's existing lane order. `groupKeys` arrives
        // as Object.keys(groupDataMap) — the static-map insertion order
        // from defaultKeys(tType), not the stored display order. Using
        // it to rebuild groupProperties[2] would reset lane sequence
        // (L1 columns and L2 rows) on every card drop. Anchor on the
        // stored list; append keys unknown to the store at the end so
        // they still get a manuallyCardSort: [] entry.
        const existingList = readList();
        const existingKeys = existingList.map(v => v.key);
        const seen = new Set(existingKeys);
        const orderedKeys = [
          ...existingKeys,
          ...groupKeys.filter(k => !seen.has(k)),
        ];
        const map = new Map(existingList.map(v => [v.key, v]));
        this.dataUpdate(() => ({
          [field]: orderedKeys.map(key => {
            if (key === groupKey) {
              const group = map.get(key);
              return group
                ? { ...group, manuallyCardSort: keys }
                : { key, hide: false, manuallyCardSort: keys };
            }
            return (
              map.get(key) ?? {
                key,
                hide: false,
                manuallyCardSort: [],
              }
            );
          }),
        }));
      },
      changeGroupHide: (key: string, hide: boolean) => {
        this.dataUpdate(() => {
          const list = [...readList()];
          const idx = list.findIndex(g => g.key === key);
          if (idx >= 0) {
            const target = list[idx];
            if (!target) return { [field]: list };
            list[idx] = { ...target, hide };
          } else {
            list.push({ key, hide, manuallyCardSort: [] });
          }
          return { [field]: list };
        });
      },
    };
  }

  groupTrait = this.traitSet(
    groupTraitKey,
    new GroupTrait(
      this.groupBy$,
      this,
      this.buildGroupOps('groupProperties'),
      'groupProperties',
      false
    )
  );

  group2Trait = this.traitSet(
    group2TraitKey,
    new GroupTrait(
      this.groupBy2$,
      this,
      this.buildGroupOps('groupProperties2'),
      'groupProperties2'
    )
  );

  isRow2Collapsed(rowKey: string): boolean {
    return this.groupCollapsed2$.value.includes(rowKey);
  }

  toggleRow2Collapsed(rowKey: string): void {
    this.dataUpdate(view => {
      const current =
        (view as { groupCollapsed2?: string[] }).groupCollapsed2 ?? [];
      const set = new Set(current);
      if (set.has(rowKey)) set.delete(rowKey);
      else set.add(rowKey);
      return { groupCollapsed2: Array.from(set) };
    });
  }

  mainProperties$ = computed(() => {
    return (
      this.data$.value?.header ?? {
        titleColumn: this.propertiesRaw$.value.find(
          property => property.type$.value === 'title'
        )?.id,
        iconColumn: 'type',
      }
    );
  });

  readonly$ = computed(() => {
    return this.manager.readonly$.value;
  });

  private getRawPropertiesWindowForRequest(
    request: DataSourceWindowRequest
  ): SingleViewPropertiesWindow {
    const sourceWindow = this.dataSource.getPropertyIdsWindow(request);
    const needShow = new Set(sourceWindow.propertyIds);
    const orderedIds: string[] = [];
    this.data$.value?.columns.forEach(column => {
      if (needShow.has(column.id)) {
        orderedIds.push(column.id);
        needShow.delete(column.id);
      }
    });
    orderedIds.push(
      ...orderKanbanPropertyIdsForDefault(this.manager.dataSource, [
        ...needShow,
      ])
    );
    const windowProperties = orderedIds.map(id => this.propertyGetOrCreate(id));
    return {
      offset: sourceWindow.offset,
      limit: sourceWindow.limit,
      totalCount: sourceWindow.totalCount,
      propertyIds: windowProperties.map(property => property.id),
      properties: windowProperties,
    };
  }

  override getPropertiesWindow(
    request: DataSourceWindowRequest
  ): SingleViewPropertiesWindow {
    const rawWindow = this.getRawPropertiesWindowForRequest(request);
    const windowProperties = rawWindow.properties.filter(
      property => !property.hide$.value
    );
    return {
      ...rawWindow,
      propertyIds: windowProperties.map(property => property.id),
      properties: windowProperties,
    };
  }

  override getBoundedPropertiesWindow(): SingleViewPropertiesWindow {
    const limit = this.propertyWindowLimit$.value;
    const offset = clampDataSourceWindowOffset(
      this.columnCount$.value,
      this.propertyWindowOffset$.value,
      limit
    );
    return this.getPropertiesWindow({ offset, limit });
  }

  override getRawPropertiesWindow(
    request: DataSourceWindowRequest
  ): SingleViewPropertiesWindow {
    return this.getRawPropertiesWindowForRequest(request);
  }

  override getBoundedRawPropertiesWindow(): SingleViewPropertiesWindow {
    const limit = this.propertyWindowLimit$.value;
    const offset = clampDataSourceWindowOffset(
      this.columnCount$.value,
      this.propertyWindowOffset$.value,
      limit
    );
    return this.getRawPropertiesWindow({ offset, limit });
  }

  get columns(): KanbanColumn[] {
    return this.propertiesRaw$.value.filter(property => !property.hide$.value);
  }

  get filter(): FilterGroup {
    return this.view?.filter ?? emptyFilterGroup;
  }

  get header() {
    return this.view?.header;
  }

  get type(): string {
    return this.view?.mode ?? 'kanban';
  }

  get view() {
    return this.data$.value;
  }

  setColumnWidth(width: number): void {
    this.dataUpdate(() => ({
      columnWidth: normalizeKanbanColumnWidth(width),
    }));
  }

  /**
   * Create a new row and assign it to a (rowKey, colKey) cell in the
   * 2-D kanban. Both L1 (column) and L2 (row) group keys are written
   * atomically so the card materializes in exactly the dropped cell.
   * Filter-condition defaults are applied on top as usual.
   */
  addCard(position: InsertToPosition, colKey: string, rowKey: string) {
    const id = this.rowAdd(position);
    this.groupTrait.addToGroup(id, colKey);
    this.group2Trait.addToGroup(id, rowKey);

    const filter = this.filter$.value;
    if (filter.conditions.length > 0) {
      const defaultValues = generateDefaultValues(filter, this.vars$.value);
      Object.entries(defaultValues).forEach(([propertyId, jsonValue]) => {
        const property = this.propertyGetOrCreate(propertyId);
        const propertyMeta = property.meta$.value;
        if (!propertyMeta) {
          return;
        }
        const value = fromJson(propertyMeta.config, {
          value: jsonValue,
          data: property.data$.value,
          dataSource: this.dataSource,
        });
        this.cellGetOrCreate(id, propertyId).valueSet(value);
      });
    }

    return id;
  }

  getHeaderCover(_rowId: string): KanbanColumn | undefined {
    const columnId = this.view?.header.coverColumn;
    if (!columnId) {
      return;
    }
    return this.propertyGetOrCreate(columnId);
  }

  getHeaderIcon(_rowId: string): KanbanColumn | undefined {
    const columnId = this.view?.header.iconColumn;
    if (!columnId) {
      return;
    }
    return this.propertyGetOrCreate(columnId);
  }

  getHeaderTitle(_rowId: string): KanbanColumn | undefined {
    const columnId = this.view?.header.titleColumn;
    if (!columnId) {
      return;
    }
    return this.propertyGetOrCreate(columnId);
  }

  hasHeader(_rowId: string): boolean {
    const hd = this.view?.header;
    if (!hd) {
      return false;
    }
    return !!hd.titleColumn || !!hd.iconColumn || !!hd.coverColumn;
  }

  isInHeader(columnId: string) {
    const hd = this.view?.header;
    if (!hd) {
      return false;
    }
    return (
      hd.titleColumn === columnId ||
      hd.iconColumn === columnId ||
      hd.coverColumn === columnId
    );
  }

  isShow(rowId: string): boolean {
    return this.isRowVisibleByFilter(rowId, this.filter$.value);
  }

  propertyGetOrCreate(columnId: string): KanbanColumn {
    return new KanbanColumn(this, columnId);
  }
}

type KanbanColumnData = KanbanViewData['columns'][number];
export class KanbanColumn extends PropertyBase {
  override move(position: InsertToPosition): void {
    this.kanbanView.dataUpdate(view => {
      const columnIndex = view.columns.findIndex(v => v.id === this.id);
      if (columnIndex < 0) {
        return {};
      }
      const columns = [...view.columns];
      const [column] = columns.splice(columnIndex, 1);
      if (!column) {
        return {};
      }
      const index = insertPositionToIndex(position, columns);
      columns.splice(index, 0, column);
      return {
        columns,
      };
    });
  }

  override hideSet(hide: boolean): void {
    this.viewDataUpdate(data => {
      return {
        ...data,
        hide,
      };
    });
  }
  hide$ = computed(() => {
    if (this.kanbanView.isInHeader(this.id)) {
      return false;
    }
    const hideFromViewData = this.viewData$.value?.hide;
    if (hideFromViewData != null) {
      return hideFromViewData;
    }
    return true;
  });

  viewData$ = computed(() => {
    return this.kanbanView.data$.value?.columns.find(v => v.id === this.id);
  });

  viewDataUpdate(
    updater: (viewData: KanbanColumnData) => Partial<KanbanColumnData>
  ): void {
    this.kanbanView.dataUpdate(data => {
      const existing = data.columns.find(v => v.id === this.id);
      if (existing) {
        return {
          ...data,
          columns: data.columns.map(v =>
            v.id === this.id ? { ...v, ...updater(v) } : v
          ),
        };
      }
      // Property not yet in columns array — create a new entry
      const newEntry: KanbanColumnData = {
        id: this.id,
        hide: false,
        ...updater({ id: this.id, hide: false }),
      };
      return {
        ...data,
        columns: [...data.columns, newEntry],
      };
    });
  }

  constructor(
    private readonly kanbanView: KanbanSingleView,
    columnId: string
  ) {
    super(kanbanView, columnId);
  }
}
